
import { useState, useEffect } from 'react';
import { addDays, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfMonth, endOfMonth, addMonths, subMonths, format, endOfDay } from 'date-fns';
import { CalendarViewMode } from '../types';
import { DateRange } from 'react-day-picker';

export const useCalendarNavigation = () => {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<{start: Date, end: Date}>({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end: endOfDay(endOfWeek(new Date(), { weekStartsOn: 1 })) // Make sure to include full Sunday
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(null);
  const [skipDateRangeUpdate, setSkipDateRangeUpdate] = useState(false);
  const [inDayNavigationMode, setInDayNavigationMode] = useState(false);

  useEffect(() => {
    if (viewMode === 'custom' || skipDateRangeUpdate || inDayNavigationMode) {
      if (skipDateRangeUpdate) {
        setSkipDateRangeUpdate(false);
      }
      return;
    }
    
    let start: Date, end: Date;
    
    switch (viewMode) {
      case 'day':
        start = new Date(currentDate);
        end = new Date(currentDate);
        break;
      case 'week':
      case 'total-work':
        start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
        end = endOfDay(addDays(start, 6)); // Make sure to include full Sunday (6 days after Monday)
        console.log(`[CALENDAR NAV] Date range for ${viewMode}:`, {
          start: format(start, 'yyyy-MM-dd HH:mm:ss'),
          end: format(end, 'yyyy-MM-dd HH:mm:ss'),
          startISO: start.toISOString(),
          endISO: end.toISOString(),
          endDay: format(end, 'EEEE') // This should be Sunday
        });
        break;
      case 'month':
        // Get the full month date range
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
        console.log(`[CALENDAR NAV] Date range for ${viewMode}:`, {
          start: format(start, 'yyyy-MM-dd HH:mm:ss'),
          end: format(end, 'yyyy-MM-dd HH:mm:ss'),
          startISO: start.toISOString(),
          endISO: end.toISOString()
        });
        break;
      default: 
        return;
    }
    
    setDateRange({ start, end });
  }, [viewMode, currentDate, skipDateRangeUpdate, inDayNavigationMode]);

  // Add a computed property to get the current view's date range
  const currentViewDates = (): { start: Date; end: Date } => {
    return dateRange;
  };

  const getDateRange = () => {
    return dateRange;
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setInDayNavigationMode(false);
    if (viewMode === 'custom') {
      setViewMode('week');
    }
  };

  const navigatePrevious = () => {
    setInDayNavigationMode(false);
    
    switch (viewMode) {
      case 'day':
        setCurrentDate(prevDate => addDays(prevDate, -1));
        break;
      case 'week':
      case 'total-work':
        setCurrentDate(prevDate => subWeeks(prevDate, 1));
        break;
      case 'month':
        setCurrentDate(prevDate => subMonths(prevDate, 1));
        break;
      case 'custom':
        const daysDiff = Math.floor((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const newStart = addDays(dateRange.start, -daysDiff);
        const newEnd = addDays(dateRange.end, -daysDiff);
        setDateRange({ start: newStart, end: newEnd });
        setCurrentDate(newStart);
        break;
    }
  };

  const navigateNext = () => {
    setInDayNavigationMode(false);
    
    switch (viewMode) {
      case 'day':
        setCurrentDate(prevDate => addDays(prevDate, 1));
        break;
      case 'week':
      case 'total-work':
        setCurrentDate(prevDate => addWeeks(prevDate, 1));
        break;
      case 'month':
        setCurrentDate(prevDate => addMonths(prevDate, 1));
        break;
      case 'custom':
        const daysDiff = Math.floor((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const newStart = addDays(dateRange.start, daysDiff);
        const newEnd = addDays(dateRange.end, daysDiff);
        setDateRange({ start: newStart, end: newEnd });
        setCurrentDate(newStart);
        break;
    }
  };

  const navigatePreviousDay = () => {
    if (viewMode === 'week') {
      setInDayNavigationMode(true);
      setDateRange(prevRange => ({
        start: addDays(prevRange.start, -1),
        end: addDays(prevRange.end, -1)
      }));
      setCurrentDate(prevDate => addDays(prevDate, -1));
      console.log('navigatePreviousDay - Updated date range:', {
        start: addDays(dateRange.start, -1),
        end: addDays(dateRange.end, -1),
        currentDate: addDays(currentDate, -1)
      });
    } else {
      setCurrentDate(prevDate => addDays(prevDate, -1));
    }
  };

  const navigateNextDay = () => {
    if (viewMode === 'week') {
      setInDayNavigationMode(true);
      setDateRange(prevRange => ({
        start: addDays(prevRange.start, 1),
        end: addDays(prevRange.end, 1)
      }));
      setCurrentDate(prevDate => addDays(prevDate, 1));
      console.log('navigateNextDay - Updated date range:', {
        start: addDays(dateRange.start, 1),
        end: addDays(dateRange.end, 1),
        currentDate: addDays(currentDate, 1)
      });
    } else {
      setCurrentDate(prevDate => addDays(prevDate, 1));
    }
  };

  const handleViewModeChange = (mode: CalendarViewMode) => {
    setInDayNavigationMode(false);
    setViewMode(mode);
    
    if (mode === 'custom' && viewMode !== 'custom') {
      setDateRange({
        start: dateRange.start,
        end: dateRange.end
      });
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setCurrentDate(date);
      setInDayNavigationMode(false);
      setIsCalendarOpen(false);
    }
  };

  const handleDateRangeSelect = (range: DateRange) => {
    if (range.from && range.to) {
      setDateRange({
        start: range.from,
        end: range.to
      });
      setCurrentDate(range.from);
      setIsCalendarOpen(false);
      setCustomDateRange(range);
      
      if (viewMode !== 'custom') {
        setViewMode('custom');
      }
      
      setInDayNavigationMode(false);
    }
  };

  return {
    viewMode,
    currentDate,
    dateRange,
    isCalendarOpen,
    customDateRange,
    handleViewModeChange,
    navigatePrevious,
    navigateNext,
    navigatePreviousDay,
    navigateNextDay,
    goToToday,
    setIsCalendarOpen,
    handleCalendarSelect,
    handleDateRangeSelect,
    getDateRange,
    setDateRange,
    currentViewDates
  };
};
